import { canAmendOffer } from "../../utils/offerAmendment";
import {
  PROPERTY_LOAN_PROCESSING_FEE_LABEL,
  propertyLoanProcessingFeeTerms,
  propertyLoanProcessingFee,
} from "../../utils/propertyLoanProcessingFee";
import { isRepaymentSettled, repaymentStatusLabel, getRepaymentStatus } from "../../utils/repaymentStatus";
import { propertyLoanName, propertyLoanTenureLabel, propertyLoanRateLabel, halfCaveatLtv } from "../../utils/propertyLoanProducts";
import { getPersonData } from "../../utils/myinfoData";
import usePropertyMarketplaceRefresh from "../../hooks/usePropertyMarketplaceRefresh";
import React, { useState, useEffect, useRef, useMemo } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { tokens } from "../../theme";
import Cookies from "js-cookie";
import LocalPoliceIcon from "@mui/icons-material/LocalPolice";
import {
  Button,
  Modal,
  message,
  Tag,
  Timeline,
  Input,
  Switch,
  Slider,
  InputNumber,
} from "antd";
import { useHistory } from "react-router-dom";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  PDFDownloadLink,
  Font,
  PDFViewer,
} from "@react-pdf/renderer";
import logo from "../../assets/img/SL-01.png";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SendIcon from "@mui/icons-material/Send";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ProfileViewer from "../../pages/borrower-dashboard/ProfileViewer";

const OFFER_STATUS_COPY = {
  pending_borrower_action: {
    label: "Awaiting borrower response",
    color: "gold",
  },
  borrower_renegotiation: {
    label: "Borrower requested changes",
    color: "purple",
  },
  borrower_rejected: {
    label: "Borrower rejected offer",
    color: "red",
  },
  awaiting_lender_documents: {
    label: "Upload documents for borrower",
    color: "blue",
  },
  pending_borrower_signature: {
    label: "Borrower reviewing documents",
    color: "green",
  },
  pending_lender_confirmation: {
    label: "Review signed documents",
    color: "cyan",
  },
  revision_requested: {
    label: "Awaiting borrower updates",
    color: "orange",
  },
  pending_disbursement: {
    label: "Pending disbursement of funds",
    color: "geekblue",
  },
  ongoing: { label: "Ongoing", color: "green" },
};

const HISTORY_ACTION_COPY = {
  offer_created: "Offer created",
  offer_updated: "Offer updated",
  accepted_offer: "Borrower accepted the offer",
  rejected_offer: "Borrower rejected the offer",
  requested_renegotiation: "Borrower requested changes",
  uploaded_initial_documents: "Documents uploaded to borrower",
  uploaded_signed_documents: "Borrower uploaded signed documents",
  reuploaded_documents: "Borrower re-uploaded documents",
  documents_confirmed: "Documents confirmed",
  revision_requested: "Revision requested",
  uploaded_document: "Document uploaded",
  borrower_confirmed_documents: "Borrower confirmed documents ready",
};

const HISTORY_COLOR_MAP = {
  lender: "blue",
  borrower: "green",
  system: "gray",
};

const PRIMARY_COLOR = "rgb(10, 61, 98)";
const PRIMARY_BUTTON_STYLE = {
  background: PRIMARY_COLOR,
  borderColor: PRIMARY_COLOR,
  color: "#fff",
};
const GLASS_CARD_BACKGROUND =
  "linear-gradient(135deg, rgba(10,61,98,0.12), rgba(255,255,255,0.95))";
const HIGHLIGHT_CARD_BACKGROUND =
  "linear-gradient(135deg, rgba(46,204,113,0.2), rgba(255,255,255,0.95))";

const YEAR_UNDATED = "undated";

const getYearKey = (rawDate) => {
  if (!rawDate) {
    return YEAR_UNDATED;
  }

  const date = new Date(rawDate);
  if (Number.isNaN(date.getTime())) {
    return YEAR_UNDATED;
  }

  return String(date.getFullYear());
};

const getYearLabel = (yearKey) =>
  yearKey === YEAR_UNDATED ? "No due date yet" : `Year ${yearKey}`;

const groupRepaymentsByYear = (rows = [], getDate = (row) => row?.due_date) => {
  const groups = new Map();
  const order = [];

  rows.forEach((row, index) => {
    const yearKey = getYearKey(getDate(row));
    if (!groups.has(yearKey)) {
      groups.set(yearKey, []);
      order.push(yearKey);
    }
    groups.get(yearKey).push({ row, index });
  });

  return order.map((year) => ({
    year,
    label: getYearLabel(year),
    items: groups.get(year),
  }));
};

const isSameYearMap = (first = {}, second = {}) => {
  const firstKeys = Object.keys(first);
  const secondKeys = Object.keys(second);
  if (firstKeys.length !== secondKeys.length) {
    return false;
  }
  return firstKeys.every((key) => second[key] === first[key]);
};

const MODAL_BODY_STYLE = {
  padding: 0,
  background: "transparent",
};

const GLASS_MODAL_PANEL_STYLE = {
  background: GLASS_CARD_BACKGROUND,
  borderRadius: 22,
  border: "1px solid rgba(10,61,98,0.12)",
  boxShadow: "0 36px 60px rgba(10,61,98,0.16)",
  backdropFilter: "blur(12px)",
  color: PRIMARY_COLOR,
  position: "relative",
};

const MODAL_MASK_STYLE = {
  backdropFilter: "blur(6px)",
  backgroundColor: "rgba(10,61,98,0.3)",
};

const formatCurrency = (value) => {
  if (value === undefined || value === null || value === "") {
    return "—";
  }
  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return value;
  }
  return numeric.toLocaleString("en-SG", {
    style: "currency",
    currency: "SGD",
    minimumFractionDigits: 2,
  });
};

const formatPercent = (value) => {
  if (value === undefined || value === null || value === "") {
    return "—";
  }
  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return value;
  }
  return `${numeric}% p.a.`;
};

const formatYears = (value) => {
  if (value === undefined || value === null || value === "") {
    return "—";
  }
  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return value;
  }
  return `${numeric} year${numeric === 1 ? "" : "s"}`;
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString("en-SG", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString("en-SG", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const safeParseJSON = (value, fallback = null) => {
  if (!value) {
    return fallback;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    console.error("Unable to parse JSON value", error);
    return fallback;
  }
};

const toTitleCase = (value = "") =>
  value
    .split(" ")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const getStatusDisplay = (status) => {
  if (!status) {
    return { label: "", color: "default" };
  }
  const config = OFFER_STATUS_COPY[status];
  if (config) {
    return config;
  }
  return {
    label: toTitleCase(status.replace(/_/g, " ")),
    color: "default",
  };
};

const getHistoryActionLabel = (action) =>
  HISTORY_ACTION_COPY[action] || toTitleCase((action || "").replace(/_/g, " "));

const getActorLabel = (actor) => {
  if (actor === "borrower") return "Borrower";
  if (actor === "lender") return "You";
  return "System";
};

const LPropLoan = () => {
  const theme = useTheme();
  const history = useHistory();
  const colors = tokens(theme.palette.mode);
  const [screenWidth, setScreenWidth] = useState(() => window.innerWidth);
  const [sortBy, setSortBy] = useState("default");
  const [allPropLoans, setAllPropLoans] = useState([]);
  const [badgeVerified, setBadgeVerified] = useState(false);
  const [backupContact, setBackupContact] = useState("");
  const [allCBSData, setAllCBSData] = useState(null);
  const [dataSingpassModal, setDataSingpassModal] = useState(false);
  const [singpassData, setSingpassData] = useState(null);
  const [viewingTab, setViewingTab] = useState(0);
  const [viewingUser, setViewingUser] = useState(0);
  const [liveUserData, setLiveUserData] = useState(null);
  const [keyman, setKeyman] = useState("");
  const [otherAuthoriserDetails, setOtherAuthoriserDetails] = useState(null);
  const [combinedSingpassData, setCombinedSingpassData] = useState([]);
  const [loanOfferOpenModal, setLoanOfferOpenModal] = useState(null);
  const [viewWholeTable, setViewWholeTable] = useState(false);
  const [minLoanAmount, setMinLoanAmount] = useState("");
  const [maxLoanAmount, setMaxLoanAmount] = useState("");
  const [maxLtv, setMaxLtv] = useState("");
  const [loanCategoryFilters, setLoanCategoryFilters] = useState([0, 1, 2]);
  const marketplaceRequestId = useRef(0);
  const [showMineOnly, setShowMineOnly] = useState(false);
  const [hideRejectedOffers, setHideRejectedOffers] = useState(false);
  const [offerAmount, setOfferAmount] = useState("");
  const [offerRatePa, setOfferRatePa] = useState("");
  const [offerTenorYears, setOfferTenorYears] = useState("");
  const [offerBusy, setOfferBusy] = useState(false);
  const [documentNotes, setDocumentNotes] = useState("");
  const [documentUploadBusy, setDocumentUploadBusy] = useState(false);
  const [scheduleDraft, setScheduleDraft] = useState([]);
  const [disbursementDate, setDisbursementDate] = useState("");
  const [reviewMessage, setReviewMessage] = useState("");
  const [reviewBusy, setReviewBusy] = useState(false);
  const [autoScheduleKey, setAutoScheduleKey] = useState(null);
  const lenderFileInputRef = useRef(null);
  const [expandedScheduleYears, setExpandedScheduleYears] = useState({});
  const [expandedDraftYears, setExpandedDraftYears] = useState({});

  // Read my existing offer for the selected loan id
  const getMyOfferForLoan = (loanId) => {
    const loan = allPropLoans.find((l) => l.id === loanId);
    return loan?.my_offer || null; // backend now provides this
  };

  // Prefill form from my_offer (if any)
  useEffect(() => {
    if (loanOfferOpenModal !== null) {
      const myOffer = getMyOfferForLoan(loanOfferOpenModal);
      if (myOffer?.offer_data) {
        setOfferAmount(myOffer.offer_data.amount ?? "");
        setOfferRatePa(myOffer.offer_data.rate_pa ?? "");
        setOfferTenorYears(myOffer.offer_data.tenor_years ?? "");
      } else {
        setOfferAmount("");
        setOfferRatePa("");
        setOfferTenorYears("");
      }

      const repaymentSource =
        myOffer?.repayment_schedules || myOffer?.repaymentSchedules || [];
      setScheduleDraft(normaliseRepayments(repaymentSource));
      setDisbursementDate(myOffer?.disbursement_date || "");
      setDocumentNotes("");
      setReviewMessage("");
      setDocumentUploadBusy(false);
      setReviewBusy(false);
      setAutoScheduleKey(null);
    }
  }, [loanOfferOpenModal]);

  const upsertOffer = async () => {
    try {
      setOfferBusy(true);

      const loan = allPropLoans.find((l) => l.id === loanOfferOpenModal);
      if (!loan) {
        message.error("Loan not found.");
        return;
      }

      const payload = {
        // include id only when editing my offer
        id: loan?.my_offer?.id || undefined,
        prop_loan_request_id: loan.id,
        offer_data: {
          amount: Number(offerAmount),
          ...propertyLoanProcessingFeeTerms(offerAmount),
          rate_pa: Number(offerRatePa), // % per annum
          tenor_years: Number(offerTenorYears), // years
        },
      };

      const resp = await fetch(
        "https://www.smart-lend.com/api/prop-loans/offers/upsert",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + Cookies.get("token"),
          },
          body: JSON.stringify(payload),
        }
      );

      const json = await resp.json();
      if (resp.status === 200 || resp.status === 201) {
        message.success(
          resp.status === 201 ? "Offer submitted." : "Offer updated."
        );
        // Refresh list so offers + my_offer are up to date
        await getAllPropLoans();
        // keep modal open, fields will update from useEffect (prefill) after refresh
      } else {
        message.error(json?.message || "Unable to save offer.");
      }
    } catch (e) {
      console.error(e);
      message.error("Something went wrong.");
    } finally {
      setOfferBusy(false);
    }
  };

  const handleCheckboxChange = (isChecked, category) => {
    if (isChecked) {
      setLoanCategoryFilters([...loanCategoryFilters, category]);
    } else {
      setLoanCategoryFilters(loanCategoryFilters.filter((c) => c !== category));
    }
  };

  const getShareholderAddress = (data) => {
    const allSingpassData = [singpassData];
    if (otherAuthoriserDetails !== null && otherAuthoriserDetails.length > 0) {
      for (const single of otherAuthoriserDetails) {
        allSingpassData.push(JSON.parse(single.singpass_data));
      }
      const toPush = allSingpassData.filter(
        (sd) => getPersonData(sd)?.name?.value === data
      );
      // console.log(toPush);
      return toPush;
    } else {
      return allSingpassData;
    }
  };

  const availableTab = [
    { name: "Personal Information" },
    { name: "Corporate Information" },
    { name: "NOA History" },
    { name: "Capitals" },
    { name: "Financials" },
    { name: "Shareholders" },
    { name: "Borrower Report" },
  ];
  const getInitials = (name) => {
    const textToSplit = name.split(" ");
    const initials =
      textToSplit?.[0]?.[0].toUpperCase() +
      (textToSplit?.[1]?.[0].toUpperCase() || "");
    return initials;
  };

  const getAllPropLoans = async () => {
    const requestId = ++marketplaceRequestId.current;
    try {
      const response = await fetch(
        "https://www.smart-lend.com/api/propTermLoan/getAllPropLoans",
        {
          method: "GET",
          headers: {
            Authorization: "Bearer " + Cookies.get("token"),
            "Content-Type": "application/json", // Add this
          },
        }
      );

      const data = await response.json();

      if (response.status === 200 && requestId === marketplaceRequestId.current) {
        setAllPropLoans(data.data);
        setLoanOfferOpenModal((id) => id !== null && !data.data.some((loan) => loan.id === id) ? null : id);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const selectedLoan =
    loanOfferOpenModal !== null
      ? allPropLoans.find((loan) => loan.id === loanOfferOpenModal)
      : null;

  const myOffer = selectedLoan?.my_offer || null;

  const lenderDocuments = myOffer?.documents
    ? myOffer.documents.filter((doc) => doc.role === "lender")
    : [];

  const borrowerDocuments = myOffer?.documents
    ? myOffer.documents.filter((doc) => doc.role === "borrower")
    : [];

  const nextRepayment = myOffer?.next_repayment;
  const repaymentSchedules = useMemo(
    () => myOffer?.repayment_schedules || myOffer?.repaymentSchedules || [],
    [myOffer]
  );
  const groupedRepaymentSchedules = useMemo(
    () => groupRepaymentsByYear(repaymentSchedules, (entry) => entry.due_date),
    [repaymentSchedules]
  );
  const groupedDraftSchedules = useMemo(
    () => groupRepaymentsByYear(scheduleDraft, (entry) => entry.due_date),
    [scheduleDraft]
  );
  const currentDraftKey = myOffer ? `offer-${myOffer.id}` : "draft-new";
  useEffect(() => {
    if (!groupedDraftSchedules.length) {
      setExpandedDraftYears((prev) => {
        if (!prev[currentDraftKey]) {
          return prev;
        }
        const next = { ...prev };
        delete next[currentDraftKey];
        return next;
      });
      return;
    }

    setExpandedDraftYears((prev) => {
      const existing = prev[currentDraftKey] || {};
      const nextMap = {};
      groupedDraftSchedules.forEach(({ year }) => {
        nextMap[year] = existing[year] !== false;
      });

      if (isSameYearMap(existing, nextMap)) {
        return prev;
      }

      return {
        ...prev,
        [currentDraftKey]: nextMap,
      };
    });
  }, [groupedDraftSchedules, currentDraftKey]);

  useEffect(() => {
    if (!myOffer || !groupedRepaymentSchedules.length) {
      return;
    }

    setExpandedScheduleYears((prev) => {
      const existing = prev[myOffer.id] || {};
      const nextMap = {};
      groupedRepaymentSchedules.forEach(({ year }) => {
        nextMap[year] = existing[year] !== false;
      });

      if (isSameYearMap(existing, nextMap)) {
        return prev;
      }

      return {
        ...prev,
        [myOffer.id]: nextMap,
      };
    });
  }, [myOffer, groupedRepaymentSchedules]);

  const normaliseRepayments = (source = []) =>
    source.map((entry) => ({
      id: entry.id ?? null,
      due_date: entry.due_date || "",
      amount_due:
        entry.amount_due !== undefined && entry.amount_due !== null
          ? String(entry.amount_due)
          : "",
      principal_amount:
        entry.principal_amount !== undefined && entry.principal_amount !== null
          ? String(entry.principal_amount)
          : "",
      interest_amount:
        entry.interest_amount !== undefined && entry.interest_amount !== null
          ? String(entry.interest_amount)
          : "",
      fee_amount:
        entry.fee_amount !== undefined && entry.fee_amount !== null
          ? String(entry.fee_amount)
          : "",
      notes: entry.notes || "",
      status: entry.status,
      paid_at: entry.paid_at,
      loan_balance:
        entry.loan_balance !== undefined && entry.loan_balance !== null
          ? String(entry.loan_balance)
          : "",
    }));

  const roundToTwo = (value) => {
    const numeric = Number(value || 0);
    if (!Number.isFinite(numeric)) {
      return 0;
    }
    return Math.round((numeric + Number.EPSILON) * 100) / 100;
  };

  const toDateInputValue = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const buildAutoSchedule = ({
    amount,
    ratePa,
    tenorYears,
    disbursementDateValue,
    paymentType,
  }) => {
    const months = Math.max(1, Math.round(tenorYears * 12));
    if (!disbursementDateValue || !months || !amount) {
      return [];
    }

    const baseDate = new Date(`${disbursementDateValue}T00:00:00`);
    if (Number.isNaN(baseDate.getTime())) {
      return [];
    }

    const monthlyRate = ratePa ? ratePa / 100 / 12 : 0;
    const interestFlat = roundToTwo(amount * monthlyRate);

    let accumulatedPrincipal = 0;
    let remainingPrincipal = amount;

    const rows = [];

    for (let index = 0; index < months; index += 1) {
      const dueDate = new Date(baseDate);
      dueDate.setMonth(baseDate.getMonth() + index + 1);

      let principalPayment = 0;
      if (paymentType === "interest") {
        principalPayment =
          index === months - 1 ? roundToTwo(remainingPrincipal) : 0;
      } else {
        const evenPrincipal = roundToTwo(amount / months);
        principalPayment =
          index === months - 1
            ? roundToTwo(amount - accumulatedPrincipal)
            : evenPrincipal;
      }

      accumulatedPrincipal = roundToTwo(
        accumulatedPrincipal + principalPayment
      );
      remainingPrincipal = roundToTwo(
        Math.max(amount - accumulatedPrincipal, 0)
      );

      const interestPayment =
        paymentType === "interest"
          ? interestFlat
          : roundToTwo(amount * monthlyRate);

      const totalPayment = roundToTwo(principalPayment + interestPayment);

      rows.push({
        due_date: toDateInputValue(dueDate),
        principal_amount: principalPayment.toFixed(2),
        interest_amount: interestPayment.toFixed(2),
        amount_due: totalPayment.toFixed(2),
        fee_amount: "",
        notes: "",
        loan_balance: remainingPrincipal.toFixed(2),
        status: "pending",
        paid_at: null,
        id: null,
      });
    }

    return rows;
  };

  const toggleDraftYear = (yearKey) => {
    setExpandedDraftYears((prev) => {
      const existing = prev[currentDraftKey] || {};
      const isOpen = existing[yearKey] !== false;
      return {
        ...prev,
        [currentDraftKey]: {
          ...existing,
          [yearKey]: !isOpen,
        },
      };
    });
  };

  const toggleRepaymentYear = (yearKey) => {
    if (!myOffer) return;
    setExpandedScheduleYears((prev) => {
      const existing = prev[myOffer.id] || {};
      const isOpen = existing[yearKey] !== false;
      return {
        ...prev,
        [myOffer.id]: {
          ...existing,
          [yearKey]: !isOpen,
        },
      };
    });
  };

  useEffect(() => {
    if (!myOffer || !disbursementDate) return;

    const offerData = myOffer.offer_data || {};
    const amountSource =
      offerAmount !== ""
        ? offerAmount
        : offerData.amount ?? offerData.amount_offer ?? "";
    const rateSource =
      offerRatePa !== ""
        ? offerRatePa
        : offerData.rate_pa ?? offerData.rate ?? "";
    const tenorSource =
      offerTenorYears !== ""
        ? offerTenorYears
        : offerData.tenor_years ?? offerData.tenor ?? "";

    const amount = Number(amountSource ?? 0);
    const ratePa = Number(rateSource ?? 0);
    const tenorYears = Number(tenorSource ?? 0);
    if (!amount || !tenorYears) return;

    const paymentType =
      selectedLoan?.loan_data?.loanPaymentType === "interest"
        ? "interest"
        : "normal";

    const baseDisbursement = myOffer.disbursement_date || "";
    const repaymentSource =
      myOffer?.repayment_schedules || myOffer?.repaymentSchedules || [];
    const hasExistingSchedule =
      Array.isArray(repaymentSource) && repaymentSource.length > 0;
    const dateChanged = disbursementDate !== baseDisbursement;

    if (!dateChanged && hasExistingSchedule) {
      if (autoScheduleKey !== null) {
        setScheduleDraft(normaliseRepayments(repaymentSource));
        setAutoScheduleKey(null);
      }
      return; // keep the saved schedule until the lender changes the date
    }

    const key = `${disbursementDate}|${amount}|${ratePa}|${tenorYears}|${paymentType}`;
    if (key === autoScheduleKey) return;

    const generated = buildAutoSchedule({
      amount,
      ratePa,
      tenorYears,
      disbursementDateValue: disbursementDate,
      paymentType,
    });

    if (!generated.length) return;

    setScheduleDraft(generated);
    setAutoScheduleKey(key);
  }, [disbursementDate, myOffer, selectedLoan, autoScheduleKey]);

  const addScheduleRow = () => {
    setScheduleDraft((prev) => [
      ...prev,
      {
        id: null,
        due_date: "",
        amount_due: "",
        principal_amount: "",
        interest_amount: "",
        fee_amount: "",
        notes: "",
        status: "pending",
        paid_at: null,
        loan_balance: "",
      },
    ]);
  };

  const updateScheduleRow = (index, field, value) => {
    setScheduleDraft((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index
          ? {
              ...row,
              [field]: value,
              ...(field === "principal_amount" ||
              field === "amount_due" ||
              field === "interest_amount"
                ? { loan_balance: "" }
                : {}),
            }
          : row
      )
    );
  };

  const removeScheduleRow = (index) => {
    setScheduleDraft((prev) =>
      prev.filter((_, rowIndex) => rowIndex !== index)
    );
  };

  const triggerLenderUpload = () => {
    lenderFileInputRef.current?.click();
  };

  const handleLenderFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length) {
      handleLenderDocumentUpload(files);
    }
    event.target.value = "";
  };

  const handleLenderDocumentUpload = async (files) => {
    if (!myOffer || files.length === 0) {
      return;
    }

    const notes = documentNotes.trim();
    let successCount = 0;

    try {
      setDocumentUploadBusy(true);

      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("type", "prop_offer");
        formData.append("file_id", myOffer.id);

        const uploadResponse = await fetch(
          "https://www.smart-lend.com/api/file/uploadUniversalFile",
          {
            method: "POST",
            headers: {
              Authorization: "Bearer " + Cookies.get("token"),
            },
            body: formData,
          }
        );

        const uploadJson = await uploadResponse.json();
        if (uploadResponse.status !== 200 || !uploadJson?.document_id) {
          throw new Error(uploadJson?.message || "Unable to upload document.");
        }

        const attachResponse = await fetch(
          `https://www.smart-lend.com/api/propTermLoan/offers/${myOffer.id}/documents`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + Cookies.get("token"),
            },
            body: JSON.stringify({
              document_id: uploadJson.document_id,
              stage: "initial",
              notes: notes || null,
            }),
          }
        );

        const attachJson = await attachResponse.json();
        if (!attachResponse.ok) {
          throw new Error(
            attachJson?.message || "Unable to register document."
          );
        }

        successCount += 1;
      }

      if (successCount > 0) {
        message.success(
          successCount === 1
            ? "Document shared with borrower."
            : `${successCount} documents shared with borrower.`
        );
        setDocumentNotes("");
        await getAllPropLoans();
      }
    } catch (error) {
      console.error(error);
      message.error(error.message || "Unable to upload document.");
    } finally {
      setDocumentUploadBusy(false);
    }
  };

  const buildReviewPayload = () => {
    return scheduleDraft.map((row) => ({
      id: row.id,
      due_date: row.due_date,
      amount_due: row.amount_due ? Number(row.amount_due) : 0,
      principal_amount: row.principal_amount
        ? Number(row.principal_amount)
        : null,
      interest_amount: row.interest_amount ? Number(row.interest_amount) : null,
      fee_amount: row.fee_amount ? Number(row.fee_amount) : null,
      notes: row.notes || null,
    }));
  };

  const handleReviewDecision = async (decision) => {
    if (!myOffer) {
      return;
    }

    const trimmedMessage = reviewMessage.trim();

    if (decision === "confirm") {
      if (!disbursementDate) {
        message.warning("Please set a disbursement date.");
        return;
      }

      if (!scheduleDraft.length) {
        message.warning("Please add at least one repayment entry.");
        return;
      }

      const invalidRepayment = scheduleDraft.some(
        (row) => !row.due_date || !row.amount_due
      );

      if (invalidRepayment) {
        message.warning(
          "Each repayment needs a due date and amount before confirming."
        );
        return;
      }
    }

    if (decision === "request_revision" && !trimmedMessage) {
      message.warning("Please let the borrower know what needs to change.");
      return;
    }

    try {
      setReviewBusy(true);

      const payload = {
        decision,
        message: trimmedMessage || null,
      };

      if (decision === "confirm") {
        payload.disbursement_date = disbursementDate;
        payload.repayments = buildReviewPayload();
      }

      const response = await fetch(
        `https://www.smart-lend.com/api/propTermLoan/offers/${myOffer.id}/review-documents`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + Cookies.get("token"),
          },
          body: JSON.stringify(payload),
        }
      );

      const json = await response.json();
      if (!response.ok) {
        throw new Error(json?.message || "Unable to update offer.");
      }

      message.success(
        decision === "confirm"
          ? "Documents confirmed."
          : "Revision request sent."
      );
      await getAllPropLoans();
      setReviewMessage("");
    } catch (error) {
      console.error(error);
      message.error(error.message || "Something went wrong.");
    } finally {
      setReviewBusy(false);
    }
  };

  const toggleRepaymentPaid = async (repayment, paid) => {
    try {
      const response = await fetch(
        `https://www.smart-lend.com/api/propTermLoan/repayments/${repayment.id}/toggle-paid`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + Cookies.get("token"),
          },
          body: JSON.stringify({
            paid,
          }),
        }
      );

      const json = await response.json();
      if (!response.ok) {
        throw new Error(json?.message || "Unable to update repayment status.");
      }

      message.success(paid ? "Repayment marked as paid." : "Status reverted.");
      await getAllPropLoans();
    } catch (error) {
      console.error(error);
      message.error(error.message || "Unable to update repayment status.");
    }
  };

  const renderHistoryTimeline = () => {
    const history = Array.isArray(myOffer?.status_history)
      ? [...myOffer.status_history].reverse()
      : [];

    if (history.length === 0) {
      return null;
    }

    const items = history.map((entry, index) => ({
      color: HISTORY_COLOR_MAP[entry.actor] || "gray",
      children: (
        <div key={`history-${myOffer.id}-${index}`}>
          <div style={{ fontWeight: 600, fontSize: "13px" }}>
            {getHistoryActionLabel(entry.action)}
          </div>
          {entry.message && (
            <div style={{ marginTop: "4px", fontSize: "13px" }}>
              {entry.message}
            </div>
          )}
          {entry.disbursement_date && (
            <div style={{ marginTop: "4px", fontSize: "12px" }}>
              Disbursement date: {formatDate(entry.disbursement_date)}
            </div>
          )}
          <div
            style={{
              marginTop: "4px",
              fontSize: "11px",
              color: "rgba(20, 44, 68, 0.6)",
            }}
          >
            {getActorLabel(entry.actor)} · {formatDateTime(entry.timestamp)}
          </div>
        </div>
      ),
    }));

    return (
      <div style={{ marginTop: 16 }}>
        <div style={{ fontWeight: 600, fontSize: "13px", color: "#142C44" }}>
          Activity
        </div>
        <Timeline style={{ marginTop: 8 }} items={items} />
      </div>
    );
  };

  const renderDocumentLinks = (title, docs) => {
    if (!docs || docs.length === 0) {
      return null;
    }

    return (
      <div style={{ marginTop: 12 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: "#142C44" }}>
          {title}
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            marginTop: 6,
          }}
        >
          {docs.map((doc) => (
            <a
              key={`doc-${doc.id}`}
              href={doc.file_url}
              target="_blank"
              rel="noreferrer"
              style={{ fontSize: 13, color: "#0a3d62" }}
            >
              {doc.file_name || "View document"}
              {doc.notes ? ` — ${doc.notes}` : ""}
            </a>
          ))}
        </div>
      </div>
    );
  };

  const renderScheduleEditor = () => {
    if (!myOffer) {
      return null;
    }

    const draftYearState = expandedDraftYears[currentDraftKey] || {};

    return (
      <div style={{ marginTop: 16 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: "#142C44" }}>
          Repayment schedule to share with borrower
        </div>
        <div
          style={{
            marginTop: 10,
            border: "1px solid rgba(0,0,0,0.08)",
            borderRadius: 8,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1.6fr 1fr 1fr 1fr 1fr 2fr 0.6fr",
              gap: 10,
              background: "rgba(10, 61, 98, 0.08)",
              padding: "8px 12px",
              fontWeight: 600,
              fontSize: 12,
            }}
          >
            <div>Due date</div>
            <div>Total</div>
            <div>Principal</div>
            <div>Interest</div>
            <div>Balance</div>
            <div>Notes</div>
            <div></div>
          </div>
          <div>
            {groupedDraftSchedules.map(({ year, label, items }) => {
              const isOpen = draftYearState[year] !== false;
              const yearTotal = items.reduce(
                (sum, { row }) => sum + Number(row.amount_due || 0),
                0
              );
              return (
                <div key={`draft-group-${year}`}>
                  <div
                    onClick={() => toggleDraftYear(year)}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "10px 12px",
                      background: "rgba(10,61,98,0.06)",
                      borderTop: "1px solid rgba(10,61,98,0.08)",
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>{label}</div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        fontSize: 12,
                        color: "rgba(10,61,98,0.7)",
                      }}
                    >
                      <span>{items.length} entries</span>
                      {yearTotal ? (
                        <span>{formatCurrency(yearTotal)}</span>
                      ) : null}
                      <span
                        style={{
                          fontWeight: 700,
                          fontSize: 18,
                          lineHeight: 1,
                        }}
                      >
                        {isOpen ? "−" : "+"}
                      </span>
                    </div>
                  </div>
                  {isOpen &&
                    items.map(({ row, index }) => (
                      <div
                        key={`schedule-${index}`}
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "1.6fr 1fr 1fr 1fr 1fr 2fr 0.6fr",
                          gap: 10,
                          padding: "8px 12px",
                          borderTop: "1px solid rgba(0,0,0,0.05)",
                          alignItems: "center",
                          background:
                            year === YEAR_UNDATED
                              ? "rgba(255,255,255,0.65)"
                              : "transparent",
                        }}
                      >
                        <input
                          type="date"
                          value={row.due_date}
                          onChange={(event) =>
                            updateScheduleRow(
                              index,
                              "due_date",
                              event.target.value
                            )
                          }
                          style={{
                            padding: "6px 8px",
                            borderRadius: 6,
                            border: "1px solid #d0d7de",
                          }}
                        />
                        <input
                          type="number"
                          min="0"
                          value={row.amount_due}
                          onChange={(event) =>
                            updateScheduleRow(
                              index,
                              "amount_due",
                              event.target.value
                            )
                          }
                          placeholder="Amount"
                          style={{
                            padding: "6px 8px",
                            borderRadius: 6,
                            border: "1px solid #d0d7de",
                          }}
                        />
                        <input
                          type="number"
                          min="0"
                          value={row.principal_amount}
                          onChange={(event) =>
                            updateScheduleRow(
                              index,
                              "principal_amount",
                              event.target.value
                            )
                          }
                          placeholder="Principal"
                          style={{
                            padding: "6px 8px",
                            borderRadius: 6,
                            border: "1px solid #d0d7de",
                          }}
                        />
                        <input
                          type="number"
                          min="0"
                          value={row.interest_amount}
                          onChange={(event) =>
                            updateScheduleRow(
                              index,
                              "interest_amount",
                              event.target.value
                            )
                          }
                          placeholder="Interest"
                          style={{
                            padding: "6px 8px",
                            borderRadius: 6,
                            border: "1px solid #d0d7de",
                          }}
                        />
                        <div style={{ padding: "6px 8px", textAlign: "right" }}>
                          {row.loan_balance
                            ? formatCurrency(row.loan_balance)
                            : "—"}
                        </div>
                        <input
                          type="text"
                          value={row.notes}
                          onChange={(event) =>
                            updateScheduleRow(
                              index,
                              "notes",
                              event.target.value
                            )
                          }
                          placeholder="Optional notes"
                          style={{
                            padding: "6px 8px",
                            borderRadius: 6,
                            border: "1px solid #d0d7de",
                          }}
                        />
                        <Button
                          danger
                          type="link"
                          onClick={() => removeScheduleRow(index)}
                          disabled={scheduleDraft.length === 1 && index === 0}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                </div>
              );
            })}
            {groupedDraftSchedules.length === 0 && (
              <div
                style={{
                  padding: "16px 12px",
                  color: "rgba(10,61,98,0.6)",
                  fontSize: 12,
                }}
              >
                No repayment rows yet. Use the button below to add the first
                instalment.
              </div>
            )}
          </div>
        </div>
        <Button
          style={{ marginTop: 10 }}
          onClick={addScheduleRow}
          type="dashed"
        >
          Add repayment row
        </Button>
      </div>
    );
  };

  const renderScheduleReadOnly = () => {
    const schedules =
      myOffer?.repayment_schedules || myOffer?.repaymentSchedules || [];

    if (!schedules.length) {
      return null;
    }

    const scheduleYearState = myOffer
      ? expandedScheduleYears[myOffer.id] || {}
      : {};

    return (
      <div style={{ marginTop: 16 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: "#142C44" }}>
          Repayment schedule
        </div>
        {myOffer.disbursement_date ? (
          <div
            style={{ fontSize: 12, marginTop: 4, color: "rgba(20,44,68,0.7)" }}
          >
            Disbursement date: {formatDate(myOffer.disbursement_date)}
          </div>
        ) : null}
        <div
          style={{
            marginTop: 10,
            border: "1px solid rgba(0,0,0,0.08)",
            borderRadius: 8,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1.6fr 1fr 1fr 1fr 1fr 1fr",
              gap: 10,
              background: "rgba(10, 61, 98, 0.08)",
              padding: "8px 12px",
              fontWeight: 600,
              fontSize: 12,
            }}
          >
            <div>Due date</div>
            <div>Total</div>
            <div>Principal</div>
            <div>Interest</div>
            <div>Balance</div>
            <div>Status</div>
          </div>
          <div>
            {groupedRepaymentSchedules.map(({ year, label, items }) => {
              const isOpen = scheduleYearState[year] !== false;
              const yearTotal = items.reduce(
                (sum, { row }) => sum + Number(row.amount_due || 0),
                0
              );
              const paidCount = items.filter(
                ({ row }) => isRepaymentSettled(row)
              ).length;

              return (
                <div key={`live-schedule-group-${year}`}>
                  <div
                    onClick={() => toggleRepaymentYear(year)}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "10px 12px",
                      background: "rgba(10,61,98,0.06)",
                      borderTop: "1px solid rgba(10,61,98,0.08)",
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>{label}</div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        fontSize: 12,
                        color: "rgba(10,61,98,0.7)",
                      }}
                    >
                      <span>
                        {paidCount}/{items.length} paid
                      </span>
                      {yearTotal ? (
                        <span>{formatCurrency(yearTotal)}</span>
                      ) : null}
                      <span
                        style={{
                          fontWeight: 700,
                          fontSize: 18,
                          lineHeight: 1,
                        }}
                      >
                        {isOpen ? "−" : "+"}
                      </span>
                    </div>
                  </div>
                  {isOpen &&
                    items.map(({ row, index }) => (
                      <div
                        key={`live-schedule-${row.id ?? `${year}-${index}`}`}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1.6fr 1fr 1fr 1fr 1fr 1fr",
                          gap: 10,
                          padding: "8px 12px",
                          borderTop: "1px solid rgba(0,0,0,0.05)",
                          alignItems: "center",
                          background:
                            isRepaymentSettled(row)
                              ? "rgba(39,174,96,0.08)"
                              : "transparent",
                        }}
                      >
                        <div>{formatDate(row.due_date)}</div>
                        <div>{formatCurrency(row.amount_due)}</div>
                        <div>
                          {row.principal_amount
                            ? formatCurrency(row.principal_amount)
                            : "—"}
                        </div>
                        <div>
                          {row.interest_amount
                            ? formatCurrency(row.interest_amount)
                            : "—"}
                        </div>
                        <div style={{ textAlign: "right" }}>
                          {row.loan_balance !== undefined &&
                          row.loan_balance !== null &&
                          row.loan_balance !== ""
                            ? formatCurrency(row.loan_balance)
                            : row.balance_after !== undefined &&
                              row.balance_after !== null
                            ? formatCurrency(row.balance_after)
                            : row.remaining !== undefined &&
                              row.remaining !== null
                            ? formatCurrency(row.remaining)
                            : "—"}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <Tag color={isRepaymentSettled(row) ? "green" : "gold"}>
                            {repaymentStatusLabel(row)}
                          </Tag>
                          {getRepaymentStatus(row) !== "paid" ? (
                            <Button
                              size="small"
                              onClick={() => toggleRepaymentPaid(row, true)}
                            >
                              Mark paid
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              onClick={() => toggleRepaymentPaid(row, false)}
                            >
                              Undo
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderFilterPanel = () => (
    <div
      style={{
        width: "100%",
        maxWidth: 300,
        background:
          "linear-gradient(180deg, rgba(10,61,98,0.08) 0%, rgba(255,255,255,0.92) 100%)",
        borderRadius: 18,
        padding: 22,
        boxShadow: "0px 18px 34px rgba(10,61,98,0.08)",
        border: "1px solid rgba(10,61,98,0.08)",
        position: "sticky",
        top: 90,
        color: PRIMARY_COLOR,
        backdropFilter: "blur(10px)",
      }}
    >
      <div style={{ fontSize: 17, fontWeight: 700, color: PRIMARY_COLOR }}>
        Filter requests
      </div>
      <div style={{ fontSize: 12, color: "rgba(10,61,98,0.65)", marginTop: 4 }}>
        Stay focused on the property-backed loans that fit your mandate.
      </div>

      <div style={{ marginTop: 20 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>My offers only</div>
            <div style={{ fontSize: 11, color: "rgba(10,61,98,0.6)" }}>
              Show applications that already include your offer.
            </div>
          </div>
          <Switch
            checked={showMineOnly}
            onChange={(checked) => setShowMineOnly(checked)}
          />
        </div>
      </div>

      <div style={{ marginTop: 18 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>Hide rejected</div>
            <div style={{ fontSize: 11, color: "rgba(10,61,98,0.6)" }}>
              Remove cards where your offer has been declined.
            </div>
          </div>
          <Switch
            checked={hideRejectedOffers}
            onChange={(checked) => setHideRejectedOffers(checked)}
          />
        </div>
      </div>

      <div style={{ marginTop: 22 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "rgba(10,61,98,0.75)",
          }}
        >
          Loan amount (S$)
        </div>
        <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
          <InputNumber
            placeholder="Min"
            min={0}
            value={minLoanAmount === "" ? null : Number(minLoanAmount)}
            onChange={(value) =>
              setMinLoanAmount(value === null ? "" : Number(value))
            }
            style={{ flex: 1 }}
          />
          <InputNumber
            placeholder="Max"
            min={0}
            value={maxLoanAmount === "" ? null : Number(maxLoanAmount)}
            onChange={(value) =>
              setMaxLoanAmount(value === null ? "" : Number(value))
            }
            style={{ flex: 1 }}
          />
        </div>
      </div>

      <div style={{ marginTop: 22 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "rgba(10,61,98,0.75)",
          }}
        >
          Maximum LTV
        </div>
        <Slider
          style={{ marginTop: 12 }}
          min={0}
          max={100}
          value={maxLtv === "" ? 100 : Number(maxLtv)}
          onChange={(value) => setMaxLtv(value === 100 ? "" : Number(value))}
        />
        <div style={{ fontSize: 11, color: "rgba(10,61,98,0.6)" }}>
          {maxLtv === "" ? "Showing all leverage levels" : `Up to ${maxLtv}%`}
        </div>
      </div>

      <div style={{ marginTop: 24 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "rgba(10,61,98,0.75)",
          }}
        >
          Loan categories
        </div>
        <div style={{ marginTop: 12, display: "grid", gap: 12 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>
                Equity Term Loan
              </div>
              <div style={{ fontSize: 11, color: "rgba(10,61,98,0.6)" }}>
                Standard property-backed financing.
              </div>
            </div>
            <Switch
              checked={loanCategoryFilters.includes(0)}
              onChange={(checked) => handleCheckboxChange(checked, 0)}
            />
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>Bridging Loan</div>
              <div style={{ fontSize: 11, color: "rgba(10,61,98,0.6)" }}>
                Short-term bridge financing requests.
              </div>
            </div>
            <Switch
              checked={loanCategoryFilters.includes(1)}
              onChange={(checked) => handleCheckboxChange(checked, 1)}
            />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>Half Caveat Loan</div>
              <div style={{ fontSize: 11, color: "rgba(10,61,98,0.6)" }}>Secured on the applicant's tenancy-in-common share.</div>
            </div>
            <Switch checked={loanCategoryFilters.includes(2)} onChange={(checked) => handleCheckboxChange(checked, 2)} />
          </div>
        </div>
      </div>

      <Button
        type="link"
        style={{ padding: 0, marginTop: 14, color: PRIMARY_COLOR }}
        onClick={() => {
          setMinLoanAmount("");
          setMaxLoanAmount("");
          setMaxLtv("");
          setLoanCategoryFilters([0, 1, 2]);
          setShowMineOnly(false);
          setHideRejectedOffers(false);
        }}
      >
        Reset filters
      </Button>
      <Button
        block
        style={{ marginTop: 10, ...PRIMARY_BUTTON_STYLE }}
        onClick={getAllPropLoans}
      >
        Refresh list
      </Button>
    </div>
  );

  const renderLoanCard = (item) => {
    const borrowerName = item.borrower_data?.principal_name || "Borrower";
    const borrowerSingpass = safeParseJSON(item.borrower_data?.singpass_data);
    const borrowerCredit = safeParseJSON(
      item.borrower_data?.credit_report_data
    );
    const loanData = item.loan_data || {};
    const totalExposure =
      Number(loanData?.oustandingLoan || 0) +
      Number(loanData?.cpfUsage || 0) +
      Number(loanData?.accruedInterest || 0);
    const propertyValuation = Number(loanData?.selectedLoanType) === 2 ? Number(loanData?.estimatedPropertyValue || 0) : Number(loanData?.valuation || 0);
    const ltv = Number(loanData?.selectedLoanType) === 2 ? halfCaveatLtv(loanData) :
      propertyValuation > 0 ? (totalExposure / propertyValuation) * 100 : null;
    const offers = Array.isArray(item.offers) ? item.offers : [];
    const previewOffers = offers.slice(0, 3);
    const hasMoreOffers = offers.length > 3;
    const myOffer = item.my_offer || null;
    const isMine = Boolean(myOffer);
    const loanTypeLabel =
      propertyLoanName(loanData?.selectedLoanType);
    const tenorLabel =
      propertyLoanTenureLabel(loanData?.selectedLoanType, loanData);
    const interestLabel =
      propertyLoanRateLabel(loanData?.selectedLoanType);
    const repaymentLabel =
      loanData?.loanPaymentType === "normal"
        ? "Interest and principal"
        : "Interest only";
    const cardBackground = isMine
      ? HIGHLIGHT_CARD_BACKGROUND
      : GLASS_CARD_BACKGROUND;
    const accentGradient = isMine
      ? "linear-gradient(90deg, rgba(39,174,96,0.95), rgba(39,174,96,0.4))"
      : "linear-gradient(90deg, rgba(10,61,98,0.9), rgba(10,61,98,0.4))";
    const createdAtCopy = item.created_at
      ? formatDateTime(item.created_at)
      : "";
    const verified = Boolean(item.borrower_data?.badge_verified);

    const handleOpenProfile = () => {
      setBadgeVerified(verified);
      setBackupContact(item.borrower_data?.user_id || "");
      const profilePayload = borrowerSingpass
        ? [
            {
              name: getPersonData(borrowerSingpass)?.name?.value || borrowerName,
              cbs_data: borrowerCredit,
            },
          ]
        : [];
      setAllCBSData(profilePayload);
      setDataSingpassModal(true);
      setLiveUserData(borrowerSingpass || null);
      setCombinedSingpassData(borrowerSingpass ? [borrowerSingpass] : []);
      setSingpassData(borrowerSingpass || null);
    };

    return (
      <div
        key={item.id}
        className="loan-item"
        style={{
          position: "relative",
          overflow: "hidden",
          background: cardBackground,
          borderRadius: 18,
          padding: 24,
          boxShadow: isMine
            ? "0 16px 36px rgba(39,174,96,0.18)"
            : "0 18px 36px rgba(10,61,98,0.12)",
          border: isMine
            ? "1px solid rgba(39,174,96,0.35)"
            : "1px solid rgba(10,61,98,0.08)",
          backdropFilter: "blur(10px)",
          color: PRIMARY_COLOR,
          display: "flex",
          flexDirection: "column",
          gap: 20,
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            height: 4,
            width: "100%",
            background: accentGradient,
          }}
        />
        <div style={{ position: "relative", zIndex: 1 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 16,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div
                onClick={handleOpenProfile}
                style={{
                  width: 60,
                  height: 60,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: 20,
                  background: "rgba(10,61,98,0.12)",
                  color: PRIMARY_COLOR,
                  cursor: "pointer",
                  position: "relative",
                  boxShadow: "0 6px 18px rgba(10,61,98,0.12)",
                }}
                title="View borrower profile"
              >
                {borrowerName ? getInitials(borrowerName) : "SL"}
                {verified && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: -4,
                      right: -4,
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      background: PRIMARY_COLOR,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      boxShadow: "0 2px 10px rgba(10,61,98,0.25)",
                    }}
                    title="SmartLend verified borrower"
                  >
                    <LocalPoliceIcon style={{ fontSize: 16 }} />
                  </div>
                )}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 17 }}>
                  {borrowerName}
                </div>
                {verified && (
                  <div
                    style={{
                      fontSize: 12,
                      color: "rgba(10,61,98,0.65)",
                      marginTop: 2,
                    }}
                  >
                    Verified borrower
                  </div>
                )}
                {createdAtCopy && (
                  <div
                    style={{
                      fontSize: 11,
                      color: "rgba(10,61,98,0.5)",
                      marginTop: 4,
                    }}
                  >
                    Listed {createdAtCopy}
                  </div>
                )}
              </div>
            </div>
            <Tag color={loanData?.selectedLoanType === 0 ? "blue" : "gold"}>
              {loanTypeLabel}
            </Tag>
          </div>
        </div>

        <div
          style={{
            position: "relative",
            zIndex: 1,
            display: "grid",
            gap: 16,
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          }}
        >
          <div>
            <div style={{ fontSize: 11, color: "rgba(10,61,98,0.6)" }}>
              Loan amount
            </div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>
              {formatCurrency(loanData?.loanAmount)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "rgba(10,61,98,0.6)" }}>
              Property valuation
            </div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>
              {formatCurrency(propertyValuation)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "rgba(10,61,98,0.6)" }}>
              Property LTV
            </div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>
              {ltv !== null ? `${ltv.toFixed(2)}%` : "—"}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "rgba(10,61,98,0.6)" }}>
              Loan tenor
            </div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{tenorLabel}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "rgba(10,61,98,0.6)" }}>
              Interest
            </div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{interestLabel}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "rgba(10,61,98,0.6)" }}>
              Payment type
            </div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>
              {repaymentLabel}
            </div>
          </div>
        </div>

        <div style={{ position: "relative", zIndex: 1 }}>
          <div style={{ fontWeight: 600 }}>
            {offers.length
              ? `${offers.length} offer${offers.length === 1 ? "" : "s"}`
              : "No offers yet"}
          </div>
          <div
            style={{
              fontSize: 12,
              color: "rgba(10,61,98,0.6)",
              marginTop: 4,
            }}
          >
            Latest terms from the marketplace
          </div>
          {previewOffers.map((offer) => {
            const offerData = offer.offer_data || {};
            const isMyOffer = myOffer && myOffer.id === offer.id;
            return (
              <div
                key={offer.id}
                style={{
                  marginTop: 10,
                  padding: "12px 14px",
                  borderRadius: 14,
                  background: isMyOffer
                    ? "rgba(39,174,96,0.15)"
                    : "rgba(10,61,98,0.08)",
                  border: isMyOffer
                    ? "1px solid rgba(39,174,96,0.4)"
                    : "1px solid rgba(10,61,98,0.08)",
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  fontSize: 13,
                }}
              >
                <div>
                  <div style={{ color: "rgba(10,61,98,0.6)", fontSize: 11 }}>
                    Amount
                  </div>
                  <div style={{ fontWeight: 700 }}>
                    {formatCurrency(offerData.amount)}
                  </div>
                </div>
                <div>
                  <div style={{ color: "rgba(10,61,98,0.6)", fontSize: 11 }}>
                    Rate (p.a.)
                  </div>
                  <div style={{ fontWeight: 700 }}>
                    {offerData.rate_pa ?? "—"}%
                  </div>
                </div>
                <div>
                  <div style={{ color: "rgba(10,61,98,0.6)", fontSize: 11 }}>
                    Tenor
                  </div>
                  <div style={{ fontWeight: 700 }}>
                    {offerData.tenor_years ?? "—"} yrs
                  </div>
                </div>
                <div>
                  <div style={{ color: "rgba(10,61,98,0.6)", fontSize: 11 }}>Processing fee</div>
                  <div style={{ fontWeight: 700 }}>{PROPERTY_LOAN_PROCESSING_FEE_LABEL}</div>
                </div>
              </div>
            );
          })}
          {hasMoreOffers && (
            <div
              style={{
                fontSize: 11,
                color: "rgba(10,61,98,0.6)",
                marginTop: 6,
              }}
            >
              +{offers.length - previewOffers.length} more offer
              {offers.length - previewOffers.length === 1 ? "" : "s"}
            </div>
          )}
          {isMine && (
            <div
              style={{
                marginTop: 12,
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <Tag color={getStatusDisplay(myOffer.status).color}>
                {getStatusDisplay(myOffer.status).label}
              </Tag>
              <div style={{ fontSize: 12, color: "rgba(10,61,98,0.7)" }}>
                You have an active offer on this request.
              </div>
            </div>
          )}
        </div>

        <div
          style={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          <Button
            type="primary"
            style={{
              ...PRIMARY_BUTTON_STYLE,
              borderRadius: 28,
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 18px",
              boxShadow: "0 12px 28px rgba(10,61,98,0.18)",
            }}
            onClick={() => setLoanOfferOpenModal(item.id)}
          >
            <span style={{ fontWeight: 700, fontSize: 15 }}>
              {myOffer ? (canAmendOffer("property", myOffer) ? "Edit offer" : "View offer") : "Make an offer"}
            </span>
            <NavigateNextIcon style={{ fontSize: 20 }} />
          </Button>
          <Button
            style={{
              borderRadius: 28,
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 18px",
              background: "rgba(10,61,98,0.12)",
              border: "1px solid rgba(10,61,98,0.18)",
              color: PRIMARY_COLOR,
              fontWeight: 700,
            }}
            onClick={() => {
              const url = `/l-messages?principelname=${
                item.borrower_data?.principal_name || ""
              }&userid=${item.borrower_data?.user_id || ""}&requestamount=${
                loanData?.loanAmount || ""
              }`;
              history.push(url);
            }}
          >
            <span style={{ fontSize: 15 }}>Message borrower</span>
            <SendIcon style={{ fontSize: 20 }} />
          </Button>
        </div>
      </div>
    );
  };

  const renderOfferWorkflow = () => {
    if (!myOffer) {
      return null;
    }

    const { label: statusLabel, color: statusColor } = getStatusDisplay(
      myOffer.status
    );

    return (
      <div style={{ marginTop: 24 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ fontWeight: 800, fontSize: 18, color: "#142C44" }}>
            Offer workflow
          </div>
          <Tag color={statusColor}>{statusLabel}</Tag>
        </div>

        <div style={{ marginTop: 12 }}>
          Processing fee: {PROPERTY_LOAN_PROCESSING_FEE_LABEL} ({formatCurrency(propertyLoanProcessingFee(myOffer.offer_data?.amount))})
        </div>

        {myOffer.borrower_notes && (
          <div
            style={{
              marginTop: 12,
              background: "rgba(20, 44, 68, 0.06)",
              padding: 12,
              borderRadius: 8,
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 600 }}>Borrower note</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>
              {myOffer.borrower_notes}
            </div>
          </div>
        )}

        {nextRepayment && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 11, opacity: 0.6 }}>Next repayment</div>
            <div style={{ fontWeight: 600 }}>
              {formatDate(nextRepayment.due_date)} ·{" "}
              {formatCurrency(nextRepayment.amount_due)}
            </div>
          </div>
        )}

        {renderDocumentLinks("Documents you've shared", lenderDocuments)}
        {renderDocumentLinks("Borrower uploads", borrowerDocuments)}

        {["awaiting_lender_documents", "pending_borrower_signature"].includes(
          myOffer.status
        ) && (
          <div
            style={{
              marginTop: 16,
              background: "rgba(10, 61, 98, 0.08)",
              padding: 14,
              borderRadius: 8,
            }}
          >
            <div style={{ fontWeight: 600, fontSize: 13 }}>
              Share documents with borrower
            </div>
            <div style={{ fontSize: 12, marginTop: 4 }}>
              Upload term sheets or agreements for the borrower to review and
              sign.
            </div>
            <Input.TextArea
              rows={2}
              placeholder="Optional note for the borrower"
              style={{ marginTop: 10 }}
              value={documentNotes}
              onChange={(event) => setDocumentNotes(event.target.value)}
            />
            <div style={{ marginTop: 10 }}>
              <Button
                type="primary"
                onClick={triggerLenderUpload}
                loading={documentUploadBusy}
              >
                {documentUploadBusy ? "Uploading..." : "Select files"}
              </Button>
              <input
                type="file"
                multiple
                accept="application/pdf,image/*"
                style={{ display: "none" }}
                ref={lenderFileInputRef}
                onChange={handleLenderFileChange}
              />
            </div>
          </div>
        )}

        {["pending_lender_confirmation", "revision_requested"].includes(
          myOffer.status
        ) && (
          <div
            style={{
              marginTop: 16,
              background: "rgba(20, 44, 68, 0.05)",
              padding: 16,
              borderRadius: 8,
            }}
          >
            <div style={{ fontWeight: 600, fontSize: 13 }}>
              Review the signed documents
            </div>
            <div style={{ fontSize: 12, marginTop: 4 }}>
              Confirm everything is signed correctly, or request a revision from
              the borrower.
            </div>
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 11, opacity: 0.6 }}>
                Disbursement date
              </div>
              <input
                type="date"
                value={disbursementDate}
                onChange={(event) => setDisbursementDate(event.target.value)}
                style={{
                  marginTop: 4,
                  padding: "6px 8px",
                  borderRadius: 6,
                  border: "1px solid #d0d7de",
                }}
              />
            </div>
            {renderScheduleEditor()}
            <div style={{ marginTop: 12 }}>
              <Input.TextArea
                rows={3}
                placeholder="Optional message to the borrower"
                value={reviewMessage}
                onChange={(event) => setReviewMessage(event.target.value)}
              />
            </div>
            <div
              style={{
                display: "flex",
                gap: 10,
                marginTop: 12,
                flexWrap: "wrap",
              }}
            >
              <Button
                type="primary"
                loading={reviewBusy}
                onClick={() => handleReviewDecision("confirm")}
              >
                Confirm documents
              </Button>
              <Button
                danger
                ghost
                loading={reviewBusy}
                onClick={() => handleReviewDecision("request_revision")}
              >
                Request revision
              </Button>
            </div>
          </div>
        )}

        {["ongoing", "funded", "pending_disbursement"].includes(myOffer.status) && renderScheduleReadOnly()}

        {renderHistoryTimeline()}
      </div>
    );
  };

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    // Call handleResize initially to set the initial screen width
    handleResize();

    // Add event listener for resize
    window.addEventListener("resize", handleResize);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  const [isMobile, setIsMobile] = useState(
    Cookies.get("ismobile") === "yes" ? true : false
  );
  useEffect(() => {
    if (screenWidth < 1200) {
      Cookies.set("ismobile", "yes");
      setIsMobile(true);
    } else {
      Cookies.set("ismobile", "no");
      setIsMobile(false);
    }
  }, [screenWidth]);

  useEffect(() => {
    getAllPropLoans();
  }, []);
  usePropertyMarketplaceRefresh(getAllPropLoans);
  return (
    <Box m="4px 20px 20px 20px">
      <Modal
        width={isMobile ? "100%" : 760}
        bodyStyle={MODAL_BODY_STYLE}
        style={{ top: isMobile ? 20 : 60 }}
        maskStyle={MODAL_MASK_STYLE}
        open={loanOfferOpenModal !== null}
        onCancel={() => {
          setLoanOfferOpenModal(null);
        }}
        footer={null}
        destroyOnClose
        closable={false}
        rootClassName="glass-modal"
        className="glass-modal"
        styles={{
          content: {
            background: "transparent",
            padding: 0,
            boxShadow: "none",
          },
          body: {
            padding: 0,
          },
        }}
      >
        {loanOfferOpenModal !== null ? (
          <div style={GLASS_MODAL_PANEL_STYLE}>
            <button
              type="button"
              className="glass-modal__close"
              onClick={() => setLoanOfferOpenModal(null)}
            >
              <CloseRoundedIcon fontSize="small" />
            </button>
            <div
              style={{
                padding: isMobile ? 18 : 28,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  color: PRIMARY_COLOR,
                  fontWeight: 900,
                  fontFamily: "Manrope, sans-serif",
                  fontSize: 20,
                }}
              >
                <h2 style={{ fontSize: "24px" }}>Make an offer</h2>
              </div>
              <div
                style={{
                  width: "80%",
                  height: "1px",
                  background: "rgba(10,61,98,0.12)",
                  borderRadius: 2,
                }}
              />
              <div style={{ marginTop: "16px" }}>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  <div style={{ width: "30%" }}>
                    <div
                      style={{ fontSize: 10, color: "grey", marginBottom: 4 }}
                    >
                      Quantum (Amount)
                    </div>
                    <input
                      type="number"
                      placeholder="e.g. 250000"
                      value={offerAmount}
                      onChange={(e) => setOfferAmount(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "8px 10px",
                        borderRadius: 8,
                        border: "1px solid #d0d7de",
                        outline: "none",
                      }}
                    />
                  </div>

                  <div style={{ width: "30%" }}>
                    <div
                      style={{ fontSize: 10, color: "grey", marginBottom: 4 }}
                    >
                      Interest (% p.a.)
                    </div>
                    <input
                      type="number"
                      placeholder="e.g. 9.5"
                      step="0.01"
                      value={offerRatePa}
                      onChange={(e) => setOfferRatePa(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "8px 10px",
                        borderRadius: 8,
                        border: "1px solid #d0d7de",
                        outline: "none",
                      }}
                    />
                  </div>

                  <div style={{ width: "30%" }}>
                    <div
                      style={{ fontSize: 10, color: "grey", marginBottom: 4 }}
                    >
                      Tenor (years)
                    </div>
                    <input
                      type="number"
                      placeholder="e.g. 3"
                      value={offerTenorYears}
                      onChange={(e) => setOfferTenorYears(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "8px 10px",
                        borderRadius: 8,
                        border: "1px solid #d0d7de",
                        outline: "none",
                      }}
                    />
                  </div>
                </div>

                <div style={{ marginTop: 12 }}>
                  Processing fee: {PROPERTY_LOAN_PROCESSING_FEE_LABEL} ({formatCurrency(propertyLoanProcessingFee(offerAmount))})
                </div>

                <div className="glass-modal__actions">
                  <button
                    className="mui_button"
                    onClick={upsertOffer}
                    disabled={offerBusy || (Boolean(myOffer) && !canAmendOffer("property", myOffer))}
                    style={{
                      borderRadius: 10,
                      padding: "8px 18px",
                      border: "none",
                      color: "white",
                      background: "rgb(14, 90, 165)",
                      fontWeight: 700,
                      cursor: offerBusy ? "not-allowed" : "pointer",
                    }}
                  >
                    {getMyOfferForLoan(loanOfferOpenModal)
                      ? offerBusy
                        ? "Updating..."
                        : "Update Offer"
                      : offerBusy
                      ? "Submitting..."
                      : "Submit Offer"}
                  </button>
                  <button
                    className="mui_button"
                    onClick={() => setLoanOfferOpenModal(null)}
                    style={{
                      borderRadius: 10,
                      padding: "8px 18px",
                      border: "1px solid #d0d7de",
                      background: "white",
                      color: "#256199",
                      fontWeight: 700,
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>

              {renderOfferWorkflow()}

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  marginTop: "20px",
                  color: "rgba(20, 44, 68, 0.8)",
                  fontWeight: 900,
                  fontFamily: "Manrope, sans-serif",
                  fontSize: 20,
                }}
              >
                <h2 style={{ fontSize: "20px" }}>Loan Request Details</h2>
              </div>
              <div
                style={{
                  width: "80%",
                  height: "1px",
                  background: "rgba(0,0,0,0.1)",
                  borderRadius: 2,
                }}
              />
              <div style={{ display: "flex" }}>
                <div style={{ width: "80%" }}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <div style={{ width: "50%" }}>
                      <div
                        style={{
                          marginTop: "15px",
                          marginBottom: "0px",
                          fontSize: "10px",
                          color: "grey",
                        }}
                      >
                        Property LTV:
                      </div>
                      <div style={{ fontWeight: 600, color: "#142C449A" }}>
                        {(
                          ((allPropLoans.filter(
                            (loa) => loa.id === loanOfferOpenModal
                          )?.[0].loan_data?.oustandingLoan +
                            allPropLoans.filter(
                              (loa) => loa.id === loanOfferOpenModal
                            )?.[0].loan_data?.cpfUsage +
                            allPropLoans.filter(
                              (loa) => loa.id === loanOfferOpenModal
                            )?.[0].loan_data?.accruedInterest) /
                            allPropLoans.filter(
                              (loa) => loa.id === loanOfferOpenModal
                            )?.[0].loan_data?.valuation) *
                          100
                        ).toFixed(2)}{" "}
                        %
                      </div>
                    </div>
                    <div style={{ width: "50%" }}>
                      <div
                        style={{
                          marginTop: "10px",
                          marginBottom: "0px",
                          fontSize: "10px",
                          color: "grey",
                        }}
                      >
                        Loan Amount:
                      </div>
                      <div style={{ fontWeight: 600, color: "#142C449A" }}>
                        {allPropLoans
                          .filter((loa) => loa.id === loanOfferOpenModal)?.[0]
                          .loan_data.loanAmount?.toLocaleString("en-US", {
                            style: "currency",
                            currency: "SGD",
                          })}
                      </div>
                    </div>
                    <div style={{ width: "50%" }}>
                      <div
                        style={{
                          marginTop: "10px",
                          marginBottom: "0px",
                          fontSize: "10px",
                          color: "grey",
                        }}
                      >
                        Property Valuation:
                      </div>
                      <div style={{ fontWeight: 600, color: "#142C449A" }}>
                        {allPropLoans
                          .filter((loa) => loa.id === loanOfferOpenModal)?.[0]
                          .loan_data.valuation?.toLocaleString("en-US", {
                            style: "currency",
                            currency: "SGD",
                          })}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <div style={{ width: "50%" }}>
                      <div
                        style={{
                          marginTop: "10px",
                          marginBottom: "0px",
                          fontSize: "10px",
                          color: "grey",
                        }}
                      >
                        Loan Tenor:
                      </div>
                      <div style={{ fontWeight: 600, color: "#142C449A" }}>
                        {propertyLoanTenureLabel(allPropLoans.find((loa) => loa.id === loanOfferOpenModal)?.loan_data?.selectedLoanType, allPropLoans.find((loa) => loa.id === loanOfferOpenModal)?.loan_data)}
                      </div>
                    </div>
                    <div style={{ width: "50%" }}>
                      <div
                        style={{
                          marginTop: "10px",
                          marginBottom: "0px",
                          fontSize: "10px",
                          color: "grey",
                        }}
                      >
                        Interest:
                      </div>
                      <div style={{ fontWeight: 600, color: "#142C449A" }}>
                        {propertyLoanRateLabel(allPropLoans.find((loa) => loa.id === loanOfferOpenModal)?.loan_data?.selectedLoanType)}
                      </div>
                    </div>
                    <div style={{ width: "50%" }}>
                      <div
                        style={{
                          marginTop: "10px",
                          marginBottom: "0px",
                          fontSize: "10px",
                          color: "grey",
                        }}
                      >
                        Loan Payment Type:
                      </div>
                      <div style={{ fontWeight: 600, color: "#142C449A" }}>
                        {allPropLoans.filter(
                          (loa) => loa.id === loanOfferOpenModal
                        )?.[0].loan_data.loanPaymentType === "normal"
                          ? "Interest and principal"
                          : "Interest Only"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ marginTop: "20px" }}>
                <div
                  style={{
                    marginTop: "10px",
                    marginBottom: "0px",
                    fontSize: "10px",
                    color: "grey",
                  }}
                >
                  Repayment Schedule:
                </div>
                <div
                  style={{
                    width: "100%",
                    borderRadius: "10px",
                    boxShadow: "0px 0px 10px rgba(0,0,0,0.1)",
                    marginTop: "10px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      background: "#142C442A",
                    }}
                  >
                    <div
                      style={{
                        width: "20%",
                        padding: "6px 10px",
                        fontWeight: "600",
                      }}
                    >
                      Month
                    </div>
                    <div
                      style={{
                        width: "20%",
                        padding: "6px 10px",
                        fontWeight: "600",
                      }}
                    >
                      Principal
                    </div>
                    <div
                      style={{
                        width: "20%",
                        padding: "6px 10px",
                        fontWeight: "600",
                      }}
                    >
                      Interest Revenue
                    </div>
                    <div
                      style={{
                        width: "20%",
                        padding: "6px 10px",
                        fontWeight: "600",
                      }}
                    >
                      Total
                    </div>
                    <div
                      style={{
                        width: "20%",
                        padding: "6px 10px",
                        fontWeight: "600",
                      }}
                    >
                      Loan Balance
                    </div>
                  </div>
                  {allPropLoans
                    .filter((loa) => loa.id === loanOfferOpenModal)?.[0]
                    .loan_data?.loanRepaymentSchedule?.slice(
                      0,
                      viewWholeTable ? undefined : 8
                    )
                    .map((repay, index) => (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          fontSize: "12px",
                          background: index % 2 ? "#142C441A" : "transparent",
                        }}
                        key={index}
                      >
                        <div style={{ width: "20%", padding: "4px 10px" }}>
                          {repay.month}
                        </div>
                        <div style={{ width: "20%", padding: "4px 10px" }}>
                          {repay.principal?.toLocaleString("en-US", {
                            style: "currency",
                            currency: "SGD",
                          })}
                        </div>
                        <div style={{ width: "20%", padding: "4px 10px" }}>
                          {repay.interest?.toLocaleString("en-US", {
                            style: "currency",
                            currency: "SGD",
                          })}
                        </div>
                        <div style={{ width: "20%", padding: "4px 10px" }}>
                          {repay.total?.toLocaleString("en-US", {
                            style: "currency",
                            currency: "SGD",
                          })}
                        </div>
                        <div style={{ width: "20%", padding: "4px 10px" }}>
                          {repay.loanBalance?.toLocaleString("en-US", {
                            style: "currency",
                            currency: "SGD",
                          })}
                        </div>
                      </div>
                    ))}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "#142C442A",
                      textAlign: "center",
                      padding: "7px",
                    }}
                    className="mui_button"
                    onClick={() => {
                      setViewWholeTable(!viewWholeTable);
                    }}
                  >
                    {viewWholeTable ? "Minimize View" : "View All"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </Modal>
      <Modal
        width={isMobile ? "100%" : 960}
        bodyStyle={MODAL_BODY_STYLE}
        style={{ top: isMobile ? 10 : 40 }}
        maskStyle={MODAL_MASK_STYLE}
        open={dataSingpassModal}
        onCancel={() => {
          setDataSingpassModal(false);
          setSingpassData(null);
        }}
        footer={null}
        destroyOnClose
        closable={false}
        rootClassName="glass-modal"
        className="glass-modal"
        styles={{
          content: {
            background: "transparent",
            padding: 0,
            boxShadow: "none",
          },
          body: {
            padding: 0,
          },
        }}
      >
        <div style={GLASS_MODAL_PANEL_STYLE}>
          <button
            type="button"
            className="glass-modal__close"
            onClick={() => {
              setDataSingpassModal(false);
              setSingpassData(null);
            }}
          >
            <CloseRoundedIcon fontSize="small" />
          </button>
          <div style={{ padding: isMobile ? 12 : 24 }}>
            {singpassData ? (
              <ProfileViewer
                keyman={keyman}
                isMobile={isMobile}
                viewingUser={viewingUser}
                setViewingUser={(data) => {
                  setViewingUser(data);
                }}
                singpassData={singpassData}
                liveUserData={liveUserData}
                otherAuthoriserDetails={otherAuthoriserDetails}
                setLiveUserData={(data) => {
                  setLiveUserData(data);
                }}
                setViewingTab={(data) => {
                  setViewingTab(data);
                }}
                viewingTab={viewingTab}
                badgeVerified={badgeVerified}
                availableTab={availableTab}
                allCBSData={allCBSData}
                combinedSingpassData={combinedSingpassData}
                getShareholderAddress={(data) => {
                  getShareholderAddress(data);
                }}
                backupContact={backupContact}
                single={true}
              />
            ) : (
              <div style={{ color: PRIMARY_COLOR }}>Loading profile…</div>
            )}
          </div>
        </div>
      </Modal>
      <Box
        className="sl-responsive-grid"
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        gridAutoRows="140px"
        gap="20px"
        borderRadius="10px"
      >
        <Box
          className="sl-responsive-panel"
          gridColumn={screenWidth > 1200 ? "span 9" : "span 12"}
          gridRow="span 2"
          borderRadius="10px"
          opacity="0.95"
          paddingBottom="100px"
          style={{
            overflowY: "scroll",
            height: isMobile ? "80vh" : "90vh",
            overflowX: "hidden",
            background:
              "transparent linear-gradient(180deg, #FFFFFFB3 0%, #FFFFFF00 100%) 0% 0% no-repeat padding-box",
            padding: "15px",
          }}
          // margin="20px 20px 20px 20px"
        >
          <Box
            className="sl-legacy-page-head"
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            borderRadius="10px"
            style={{ marginBottom: "10px" }}
          >
            <Typography
              color={colors.grey[920]}
              variant="h5"
              fontWeight="800"
              fontFamily="Manrope, sans-serif"
              fontSize="18px"
            >
              Requested Loans
            </Typography>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <label
                  htmlFor="sort-by"
                  style={{
                    marginRight: "7px",
                    marginLeft: "10px",
                    color: "#142C44CC",
                    fontWeight: "800",
                    fontSize: "13px",
                    fontFamily: "Manrope, sans-serif",
                  }}
                >
                  Sort by:
                </label>
                <div style={{ width: "63%" }}>
                  <select
                    id="sort-by"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    style={{
                      width: "100%",
                      borderRadius: "30px",
                      color: "#0E5AA5",
                      backgroundColor: "#ECF0F5",
                      border: "1px solid #3E536733",
                      fontWeight: "600",
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "13px",
                    }}
                  >
                    <option value="default">Recommend</option>
                    <option value="latest-request">Latest Request</option>
                  </select>
                </div>
              </div>
            </div>
          </Box>
          <div
            className="sl-property-marketplace-grid"
            style={{
              display: "grid",
              gap: 20,
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            }}
          >
            {allPropLoans
              .filter((loan) => {
                const loanAmount = loan.loan_data?.loanAmount || 0;

                // Filter by loan amount range
                const meetsMinAmount =
                  minLoanAmount === "" || loanAmount >= Number(minLoanAmount);
                const meetsMaxAmount =
                  maxLoanAmount === "" || loanAmount <= Number(maxLoanAmount);
                const meetMaxLtv =
                  maxLtv === "" ||
                  (Number(loan.loan_data?.selectedLoanType) === 2 ? (halfCaveatLtv(loan.loan_data) ?? Infinity) : ((loan.loan_data?.oustandingLoan +
                    loan.loan_data?.cpfUsage +
                    loan.loan_data?.accruedInterest) /
                    loan.loan_data?.valuation) *
                    100) <= Number(maxLtv);

                // Filter by loan category
                const meetsCategory =
                  loanCategoryFilters.length === 0 ||
                  loanCategoryFilters.includes(
                    loan.loan_data?.selectedLoanType
                  );
                const meetsMineFilter = !showMineOnly || Boolean(loan.my_offer);
                const offerStatus = loan.my_offer?.status || "";
                const meetsRejectedFilter =
                  !hideRejectedOffers ||
                  !offerStatus.toLowerCase().includes("rejected");

                return (
                  meetsMinAmount &&
                  meetsMaxAmount &&
                  meetsCategory &&
                  meetMaxLtv &&
                  meetsMineFilter &&
                  meetsRejectedFilter
                );
              })
              .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) // Sort by latest created_at
              .map((item) => renderLoanCard(item))}
          </div>
        </Box>
        <details className="sl-marketplace-filters sl-property-marketplace-filters" open={!isMobile}>
          <summary>Search filters</summary>
          <Box
            gridColumn="span 3"
            display="flex"
            justifyContent="center"
            alignItems="flex-start"
            padding="20px"
            style={{ height: "90vh" }}
          >
            {renderFilterPanel()}
          </Box>
        </details>
      </Box>
    </Box>
  );
};

export default LPropLoan;
